import React from 'react';
import {
  FileText, X, Loader2, CheckCircle, Download, BarChart3,
  CircleDot, Clock, Archive, Hash, RefreshCw, Mail, FileDown
} from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';

/**
 * Report Modal - Displays report details and generated data
 * Extracted from ReportsView.jsx for modularity
 */
function ReportModal({
  report,
  generatedData,
  generating,
  generatedReportId,
  downloadingReport,
  downloadingPDF,
  onClose,
  onGenerate,
  onExportCSV,
  onExportPDF,
  onEmail,
  onRegenerate,
  onDirectExport
}) {
  const { t, fmt } = useFormat();
  if (!report) return null;
  const today = fmt.date(new Date());

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-modal-backdrop
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-blue-100">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{report.name}</h2>
                <p className="text-sm text-gray-500">{t('reports.modal.reportOf', { date: today })}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-auto p-6">
          {!generatedData ? (
            <div className="py-4">
              {/* Value Summary - "What's inside" */}
              {report.summary && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
                    {t('reports.modal.includes')}
                  </h4>
                  <ul className="space-y-2">
                    {report.summary.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CircleDot className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Audience & Data Scope */}
              <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                {report.audience && (
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {t('reports.modal.audience')}
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {report.audience}
                    </p>
                  </div>
                )}
                {report.dataScope && (
                  <div className="flex-1 min-w-[150px]">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {t('reports.modal.dataSource')}
                    </p>
                    <p className="text-sm text-gray-900 font-medium flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-green-600" />
                      {report.dataScope}
                    </p>
                  </div>
                )}
              </div>

              {/* Generate CTA */}
              <div className="text-center pt-2">
                {report.isDirectExport && onDirectExport ? (
                  <>
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={() => onDirectExport('excel')}
                        disabled={generating}
                        className={`inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold transition-all shadow-sm ${
                          generating
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-md'
                        }`}
                      >
                        {generating ? (
                          <><Loader2 className="w-5 h-5 animate-spin" /> {t('reports.modal.exporting')}</>
                        ) : (
                          <><Download className="w-5 h-5" /> Excel</>
                        )}
                      </button>
                      <button
                        onClick={() => onDirectExport('pdf')}
                        disabled={generating}
                        className={`inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold transition-all shadow-sm ${
                          generating
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-red-600 text-white hover:bg-red-700 hover:shadow-md'
                        }`}
                      >
                        {generating ? (
                          <><Loader2 className="w-5 h-5 animate-spin" /> {t('reports.modal.exporting')}</>
                        ) : (
                          <><FileDown className="w-5 h-5" /> PDF</>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-3">
                      {t('reports.modal.dataAsOf', { date: today })} • {new Date().getFullYear()}
                    </p>
                  </>
                ) : (
                  <>
                    <button
                      onClick={onGenerate}
                      disabled={generating}
                      className={`inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold transition-all shadow-sm ${
                        generating
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md'
                      }`}
                    >
                      {generating ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> {t('reports.modal.generating')}</>
                      ) : (
                        <><BarChart3 className="w-5 h-5" /> {report.buttonLabel || t('reports.modal.generate')}</>
                      )}
                    </button>
                    <p className="text-xs text-gray-400 mt-3">
                      {t('reports.modal.willUseDataAsOf', { date: today })}
                    </p>
                    <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1">
                      <Archive className="w-3 h-3" />
                      {t('reports.modal.archivedSha256')}
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">{t('reports.modal.records', { count: generatedData.length })}</span>
                  {generatedReportId && (
                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      ID: {generatedReportId}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={onExportCSV}
                    disabled={downloadingReport}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                  >
                    {downloadingReport ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> {t('reports.modal.downloading')}</>
                    ) : (
                      <><Download className="w-4 h-4" /> CSV</>
                    )}
                  </button>
                  {onExportPDF && (
                    <button
                      onClick={onExportPDF}
                      disabled={downloadingPDF}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400"
                    >
                      {downloadingPDF ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> PDF...</>
                      ) : (
                        <><FileDown className="w-4 h-4" /> PDF</>
                      )}
                    </button>
                  )}
                  {onEmail && (
                    <button
                      onClick={onEmail}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title={t('reports.modal.emailTitle')}
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </button>
                  )}
                </div>
              </div>

              {/* Archive notice when report is saved */}
              {generatedReportId && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                  <Archive className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <strong>{t('reports.modal.archivedLabel')}</strong> - {t('reports.modal.archivedText')}
                  </div>
                </div>
              )}

              {/* Data Table */}
              <div className="border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        {generatedData.length > 0 && Object.keys(generatedData[0]).map((key, i) => (
                          <th key={i} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {generatedData.slice(0, 20).map((row, rowIdx) => (
                        <tr key={rowIdx} className="hover:bg-gray-50">
                          {Object.values(row).map((val, colIdx) => (
                            <td key={colIdx} className="px-4 py-3 text-sm text-gray-700">
                              {val === 'URGENT' || val === 'RETARD' ? (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">{val}</span>
                              ) : val === 'A surveiller' || val === 'A suivre' ? (
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">{val}</span>
                              ) : val === 'OK' || val === 'Normal' ? (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">{val}</span>
                              ) : val || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {generatedData.length > 20 && (
                  <div className="px-4 py-3 bg-gray-50 text-sm text-gray-500 text-center">
                    {t('reports.modal.limitedRows', { count: 20 })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            {t('common.actions.close')}
          </button>
          {generatedData && (
            <button onClick={onRegenerate} className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              {t('reports.modal.regenerate')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportModal;
